/**
 * ConversationStore
 * 
 * Unified store for canonical messages with embedded tool execution state.
 * Replaces separate message and tool state tracking.
 * 
 * Key features:
 * - Per-thread ordered message lists
 * - O(1) tool lookup via index
 * - Auto-persistence with dirty tracking
 * - Real-time subscriptions
 */

import { createFileLogger } from 'utils/logger';
import { CanonicalMessage, CanonicalContent, ToolExecutionState, ToolCallContent } from '../types/CanonicalMessage';
import { emitEvent } from 'services/worldEventsService';

const log = createFileLogger('CONVERSATION_STORE', {enabled: false});

type Callback = (messages: CanonicalMessage[]) => void;

interface ThreadData {
  messageIds: string[];
  messages: Map<string, CanonicalMessage>;
}

interface ToolLocation {
  threadId: string;
  messageId: string;
}

export class ConversationStore {
  // Core state
  private threads = new Map<string, ThreadData>();
  private subscribers = new Map<string, Set<Callback>>();
  
  // Performance: Fast tool lookup
  public toolIndex = new Map<string, ToolLocation>();
  
  // Defensive state
  private abortControllers = new Map<string, AbortController>();
  private persistenceQueue = new Map<string, Promise<void>>();
  private dirtyMessages = new Set<string>();
  
  // Reconciliation hooks
  private onReconcileCallbacks: Array<(threadId: string) => void> = [];
  
  // Streaming session buffers (per-thread)
  // Buffers rapid text updates and flushes to UI at controlled rate
  private streamingBuffers = new Map<string, {
    hasPendingUpdates: boolean;
    flushScheduled: boolean;
    flushCount: number;
    lastFlushTime: number;
  }>();
  private readonly FLUSH_INTERVAL_MS = 16; // 16ms = 60fps for smooth visible streaming

  // Monotonically-increasing version counter per thread.
  // scheduleBufferedFlush captures the version at scheduling time and skips the
  // notify if no mutation happened between schedule and fire.
  private threadVersions = new Map<string, number>();

  private bumpVersion(threadId: string): void {
    this.threadVersions.set(threadId, (this.threadVersions.get(threadId) ?? 0) + 1);
  }
  
  /**
   * Get ordered messages for a thread
   */
  getMessages(threadId: string): CanonicalMessage[] {
    const thread = this.threads.get(threadId);
    if (!thread) {
      log.log(`[ConversationStore] 🔍 getMessages: Thread ${threadId} not found, returning empty array`);
      return [];
    }
    
    const messages = thread.messageIds
      .map(id => thread.messages.get(id))
      .filter(Boolean) as CanonicalMessage[];
    const messagesWithWrongThread = messages.filter(m => m.threadId && m.threadId !== threadId);
    if (messagesWithWrongThread.length > 0) {
      console.error(`[ConversationStore] ⚠️⚠️⚠️ THREAD CONTAMINATION in store! Thread ${threadId} contains ${messagesWithWrongThread.length} messages from other threads:`, {
        requestedThreadId: threadId,
        wrongThreadIds: [...new Set(messagesWithWrongThread.map(m => m.threadId))],
        wrongMessageIds: messagesWithWrongThread.slice(0, 5).map(m => m.id)
      });
      
      // Filter out contaminated messages as a safety net
      const cleanMessages = messages.filter(m => !m.threadId || m.threadId === threadId);
      log.log(`[ConversationStore] ✅ Filtered ${messagesWithWrongThread.length} contaminated messages, returning ${cleanMessages.length} clean messages`);
      return cleanMessages;
    }
    
    log.log(`[ConversationStore] 🔍 getMessages: Returning ${messages.length} messages for thread ${threadId}`);
    const toolResultMessages = messages.filter(m => 
      m.role === 'user' && 
      m.content.some((c: any) => c.type === 'tool_result')
    );
    if (toolResultMessages.length > 0) {
      log.log(`[ConversationStore] 🔍 Found ${toolResultMessages.length} messages with tool_results:`, {
        messageIds: toolResultMessages.map(m => m.id.substring(0, 20) + '...'),
        toolUseIds: toolResultMessages.flatMap(m => 
          m.content
            .filter((c: any) => c.type === 'tool_result')
            .map((c: any) => c.tool_use_id || c.tool_call_id || 'NO_ID')
        )
      });
    }
    
    return messages;
  }
  
  /**
   * Get a specific message by ID
   */
  getMessage(threadId: string, messageId: string): CanonicalMessage | null {
    const thread = this.threads.get(threadId);
    return thread?.messages.get(messageId) || null;
  }
  
  /**
   * Add message to thread
   */
  addMessage(threadId: string, message: CanonicalMessage, skipPersist = false): void {
    let thread = this.threads.get(threadId);
    if (!thread) {
      thread = { messageIds: [], messages: new Map() };
      this.threads.set(threadId, thread);
    }
    
    // Check if message already exists (idempotency)
    if (thread.messages.has(message.id)) {
      console.warn('[ConversationStore] Message already exists:', message.id);
      return;
    }
    // Old messages from DB may have tool_call_id, but Claude API requires tool_use_id
    const normalizedMessage = this.normalizeToolResultFields(message);
    
    thread.messages.set(normalizedMessage.id, normalizedMessage);
    thread.messageIds.push(normalizedMessage.id);
    
    // Index any tool calls
    normalizedMessage.content.forEach((block: CanonicalContent) => {
      if (block.type === 'tool_call') {
        this.toolIndex.set(block.id, { threadId, messageId: normalizedMessage.id });
      }
    });
    if (normalizedMessage.role === 'user' && normalizedMessage.content.some((c: any) => c.type === 'tool_result')) {
      log.log('[ConversationStore] 🔍 Added message with tool_result:', {
        messageId: normalizedMessage.id,
        role: normalizedMessage.role,
        contentBlocks: normalizedMessage.content.map((c: any) => ({
          type: c.type,
          hasToolUseId: 'tool_use_id' in c,
          hasToolCallId: 'tool_call_id' in c,
          toolUseId: c.tool_use_id,
          toolCallId: c.tool_call_id
        }))
      });
    }
    
    // Empty assistant placeholders (content: []) are created at the start of a stream.
    // Use the buffered flush so SpaceChat doesn't re-render for a contentless message —
    // the first text_delta will arrive within milliseconds and trigger the real render.
    if (normalizedMessage.role === 'assistant' && normalizedMessage.content.length === 0) {
      this.scheduleBufferedFlush(threadId);
    } else {
      this.notifySubscribers(threadId);
    }

    // Persist user messages only — assistant messages are persisted by tool-runtime
    // (appendMessageToConversation in agentLoop.ts) to avoid duplicate MongoDB writes.
    if (!skipPersist && normalizedMessage.role !== 'assistant') {
      this.persistMessage(threadId, normalizedMessage.id);
    }

    // Write non-empty, non-streaming messages to the conversation Y.Doc.
    // User messages are always complete at creation; assistant messages are written
    // in flushPendingUpdates() once the stream is finished.
    // skipPersist=true means the message came FROM the Y.Doc — don't write back.
    if (!skipPersist && normalizedMessage.content.length > 0 && normalizedMessage.role !== 'assistant') {
      import('./conversationYjsStore').then(({ writeMessageToYDoc }) => {
        writeMessageToYDoc(threadId, normalizedMessage);
      }).catch(() => {});
    }

    // Emit message.sent for real user messages (not tool results, not assistant messages)
    if (
      !skipPersist &&
      normalizedMessage.role === 'user' &&
      normalizedMessage.content.length > 0 &&
      !normalizedMessage.content.some((c: any) => c.type === 'tool_result')
    ) {
      try {
        const textBlock = normalizedMessage.content.find((c: any) => c.type === 'text');
        emitEvent('message.sent', {
          threadId,
          messageId: normalizedMessage.id,
          preview: (textBlock as any)?.text?.slice(0, 100) || '',
        });
      } catch { /* non-fatal */ }
    }
  }
  
  /**
   * Normalize tool_result field names (tool_call_id → tool_use_id)
   * Old messages in DB use tool_call_id, but Claude API requires tool_use_id
   */
  private normalizeToolResultFields(message: CanonicalMessage): CanonicalMessage {
    if (!message.content || !Array.isArray(message.content)) {
      return message;
    }
    
    // Check if any normalization is needed
    let needsNormalization = false;
    const normalizedContent = message.content.map((block: any) => {
      if (block.type === 'tool_result' && block.tool_call_id && !block.tool_use_id) {
        needsNormalization = true;
        log.log('[ConversationStore] 🔧 Normalizing tool_result: tool_call_id → tool_use_id:', {
          messageId: message.id?.substring(0, 20) + '...',
          oldFieldValue: block.tool_call_id
        });
        
        // Create new block with correct field name
        const { tool_call_id, ...rest } = block;
        return {
          ...rest,
          tool_use_id: tool_call_id  // Migrate to correct field
        };
      }

      return block;
    });
    if (!needsNormalization) {
      return message;  // Return original reference
    }
    
    return {
      ...message,
      content: normalizedContent
    };
  }
  
  /**
   * Normalize all existing messages in a thread (fix legacy data)
   * Call this to migrate old messages that are already in the store
   */
  normalizeExistingMessages(threadId: string): void {
    const thread = this.threads.get(threadId);
    if (!thread) return;
    
    log.log('[ConversationStore] 🔧 Starting normalization of existing messages:', {
      threadId,
      totalMessages: thread.messageIds.length
    });
    
    let normalizedCount = 0;
    const normalizedDetails: Array<{ messageId: string; role: string; changes: number }> = [];
    
    // Update all messages in place
    for (const [messageId, message] of thread.messages.entries()) {
      const normalizedMessage = this.normalizeToolResultFields(message);
      
      // Only update if something changed
      if (normalizedMessage !== message) {
        thread.messages.set(messageId, normalizedMessage);
        normalizedCount++;
        
        // Count how many blocks were changed
        const changedBlocks = message.content.filter((block: any, idx: number) => {
          const normalizedBlock = normalizedMessage.content[idx] as any;
          return block.type === 'tool_result' && 
                 block.tool_call_id && 
                 !block.tool_use_id &&
                 normalizedBlock.tool_use_id;
        }).length;
        
        normalizedDetails.push({
          messageId: messageId.substring(0, 20) + '...',
          role: message.role,
          changes: changedBlocks
        });
      }
    }
    
    if (normalizedCount > 0) {
      log.log(`[ConversationStore] ✅ Normalized ${normalizedCount} existing messages in thread ${threadId}:`, normalizedDetails);
      this.notifySubscribers(threadId);
    } else {
      log.log('[ConversationStore] ✅ No normalization needed - all messages already have correct field names');
    }
  }
  
  /**
   * Remove a message from a thread
   */
  removeMessage(threadId: string, messageId: string): void {
    const thread = this.threads.get(threadId);
    if (!thread) return;
    
    // Remove from messages map
    thread.messages.delete(messageId);
    
    // Remove from message IDs array
    thread.messageIds = thread.messageIds.filter(id => id !== messageId);
    
    // Remove any tool index entries for this message
    for (const [toolId, location] of this.toolIndex.entries()) {
      if (location.messageId === messageId) {
        this.toolIndex.delete(toolId);
      }
    }
    
    log.log('[ConversationStore] Message removed:', messageId);
    
    // Notify subscribers
    this.notifySubscribers(threadId);
  }
  
  /**
   * Update an existing message
   * Note: Caller is responsible for calling notifySubscribers if needed
   */
  updateMessage(threadId: string, messageId: string, updater: (msg: CanonicalMessage) => void): void {
    const thread = this.threads.get(threadId);
    const message = thread?.messages.get(messageId);
    if (!message) {
      console.error('[ConversationStore] Message not found for update:', messageId);
      return;
    }

    // Clone the message so React.memo detects the reference change.
    const cloned: CanonicalMessage = { ...message, content: [...(message.content as any[])] };
    updater(cloned);
    thread!.messages.set(messageId, cloned);
    // Note: Don't notify here - let caller decide (immediate vs buffered)
  }
  
  /**
   * Append text to a message (for streaming)
   * Uses buffered flush for smooth visual streaming
   * 
   * Note: Appends to the LAST text block to support multiple text blocks per message
   */
  appendTextToMessage(threadId: string, messageId: string, text: string): void {
    const thread = this.threads.get(threadId);
    const message = thread?.messages.get(messageId);
    if (!message) {
      console.error('[ConversationStore] Message not found for text append:', messageId);
      return;
    }

    // Build new content array so React.memo detects the changed message reference.
    const existingContent = message.content as any[];
    const lastIdx = existingContent.length - 1;
    const lastBlock = existingContent[lastIdx];
    const newContent = lastBlock?.type === 'text'
      // Replace last block with new text (new object reference)
      ? [...existingContent.slice(0, lastIdx), { ...lastBlock, text: (lastBlock.text ?? '') + text }]
      // No text block yet — append one
      : [...existingContent, { type: 'text', text }];

    // Replace the message with a new object reference so React.memo picks up the change.
    const updatedMessage = { ...message, content: newContent };
    thread!.messages.set(messageId, updatedMessage);

    // Mark as dirty for persistence (but don't persist on every character!)
    this.dirtyMessages.add(messageId);

    // Use buffered flush for smooth text streaming (16ms = 60fps)
    this.scheduleBufferedFlush(threadId);
  }
  
  /**
   * Finalize current text block (no-op marker for clarity)
   * Text before tool will be in separate block from text after tool
   */
  finalizeCurrentTextBlock(threadId: string, messageId: string): void {
    // No action needed - startNewTextBlock will create separation
  }
  
  /**
   * Start a new text block for subsequent text appends
   * Used after adding a tool call - ensures text after tool is separate
   */
  startNewTextBlock(threadId: string, messageId: string): void {
    const thread = this.threads.get(threadId);
    const message = thread?.messages.get(messageId);
    if (!message) return;

    const newContent = [...(message.content as any[]), { type: 'text' as const, text: '' }];
    thread!.messages.set(messageId, { ...message, content: newContent });
    log.log('[ConversationStore] 📍 Started new text block, total content blocks:', newContent.length);

    this.scheduleBufferedFlush(threadId);
  }
  
  /**
   * Replace the content of the last text block with the provided text.
   * Used to repair a message whose streamed text was garbled due to missing
   * events (sequence gaps). The `finalMessage` from the `done` event, which
   * the tool-runtime always sends as the full last-iteration response, is used
   * as the authoritative content.
   */
  repairLastTextBlock(threadId: string, messageId: string, text: string): void {
    const thread = this.threads.get(threadId);
    const message = thread?.messages.get(messageId);
    if (!message) return;

    const content = message.content as any[];
    const lastTextIdx = content.map((c: any) => c.type).lastIndexOf('text');
    if (lastTextIdx === -1) return;

    log.log(`[ConversationStore] repairLastTextBlock: replacing ${content[lastTextIdx].text?.length ?? 0} chars with ${text.length} chars from finalMessage`);
    const newContent = [
      ...content.slice(0, lastTextIdx),
      { ...content[lastTextIdx], text },
      ...content.slice(lastTextIdx + 1),
    ];
    thread!.messages.set(messageId, { ...message, content: newContent });
    this.scheduleBufferedFlush(threadId);
  }

  /**
   * Clear the text in the last text block (for max_tokens retry)
   * Called when a truncated stream is about to be retried so the partial text
   * from the failed attempt doesn't prepend to the retry's output.
   */
  resetLastTextBlock(threadId: string, messageId: string): void {
    const thread = this.threads.get(threadId);
    const message = thread?.messages.get(messageId);
    if (!message) return;

    const content = message.content as any[];
    const lastTextIdx = content.map((c: any) => c.type).lastIndexOf('text');
    if (lastTextIdx === -1) return;

    log.log(`[ConversationStore] text_reset: clearing ${content[lastTextIdx].text?.length ?? 0} chars of partial text from max_tokens retry`);
    const newContent = [
      ...content.slice(0, lastTextIdx),
      { ...content[lastTextIdx], text: '' },
      ...content.slice(lastTextIdx + 1),
    ];
    thread!.messages.set(messageId, { ...message, content: newContent });
    this.scheduleBufferedFlush(threadId);
  }

  /**
   * Schedule a buffered flush to UI (throttled for smooth streaming)
   */
  private scheduleBufferedFlush(threadId: string): void {
    this.bumpVersion(threadId);

    let buffer = this.streamingBuffers.get(threadId);
    if (!buffer) {
      buffer = { hasPendingUpdates: false, flushScheduled: false, flushCount: 0, lastFlushTime: 0 };
      this.streamingBuffers.set(threadId, buffer);
    }
    
    buffer.hasPendingUpdates = true;
    
    // If flush already scheduled it will pick up the latest version
    if (buffer.flushScheduled) {
      return;
    }
    
    buffer.flushScheduled = true;

    // Capture the version at scheduling time. If no additional mutations fire
    // before the timeout, the version will still match and we notify normally.
    // The version check is always true when hasPendingUpdates is true, but
    // it guards against a future code path that calls notify without mutations.
    const scheduledVersion = this.threadVersions.get(threadId) ?? 0;
    const bufferRef = buffer;
    setTimeout(() => {
      bufferRef.flushScheduled = false;
      if (bufferRef.hasPendingUpdates) {
        bufferRef.hasPendingUpdates = false;
        bufferRef.flushCount++;
        bufferRef.lastFlushTime = Date.now();
        // Only notify if the thread actually changed since this flush was scheduled
        const currentVersion = this.threadVersions.get(threadId) ?? 0;
        if (currentVersion >= scheduledVersion) {
          this.notifySubscribers(threadId);
        }
      }
    }, this.FLUSH_INTERVAL_MS);
  }
  
  /**
   * Flush any pending buffered updates immediately
   * Call this when stream completes to ensure final text appears
   */
  flushPendingUpdates(threadId: string): void {
    const buffer = this.streamingBuffers.get(threadId);
    if (buffer?.hasPendingUpdates) {
      buffer.flushCount++;
      log.log(`[ConversationStore] 🔥 FINAL Flush #${buffer.flushCount} → UI (stream complete)`);
      buffer.hasPendingUpdates = false;
      buffer.flushScheduled = false;
      this.notifySubscribers(threadId);
      // Reset buffer for next stream
      buffer.flushCount = 0;
    }

    // Write the finalized assistant message to the local conversation Y.Doc (IDB cache).
    // Backend persistence is handled by tool-runtime (appendMessageToConversation in agentLoop.ts).
    const thread = this.threads.get(threadId);
    if (thread) {
      const lastMsgId = thread.messageIds[thread.messageIds.length - 1];
      const lastMsg = lastMsgId ? thread.messages.get(lastMsgId) : undefined;
      if (lastMsg?.role === 'assistant' && lastMsg.content.length > 0) {
        import('./conversationYjsStore').then(({ writeMessageToYDoc }) => {
          writeMessageToYDoc(threadId, lastMsg);
        }).catch(() => {});
      }
    }
  }
  
  /**
   * Force immediate backend persistence of a message (bypass debounce)
   * Use for critical messages that must be persisted before continuation
   */
  async persistMessageImmediate(threadId: string, messageId: string): Promise<void> {
    const thread = this.threads.get(threadId);
    const message = thread?.messages.get(messageId);
    if (!message) {
      console.warn('[ConversationStore] ⚠️ Cannot persist - message not found:', messageId);
      return;
    }
    
    log.log('[ConversationStore] 🔥 FORCE IMMEDIATE persistence:', {
      messageId: messageId.substring(0, 20) + '...',
      role: message.role,
      contentLength: message.content.length
    });
    
    // Skip debounce - call backend directly
    try {
      await this.persistToBackend(threadId, message);
      // Clear from dirty set since we just persisted
      this.dirtyMessages.delete(messageId);
      log.log('[ConversationStore] ✅ Immediate persistence complete');
    } catch (error) {
      console.error('[ConversationStore] ❌ Immediate persistence failed:', error);
      throw error;
    }
  }
  
  /**
   * Helper: Actually persist message to backend (extracted for reuse)
   */
  private async persistToBackend(threadId: string, message: CanonicalMessage): Promise<void> {
    // Validation checks (same as debouncedPersist)
    if (message.role === 'assistant' && message.content.length === 0) {
      console.warn('[ConversationStore] ⏭️ Skipping persistence of empty assistant message');
      return;
    }
    
    const hasNonEmptyContent = message.content.some((c: any) => {
      if (c.type === 'text') {
        return c.text && c.text.trim().length > 0;
      }
      if (c.type === 'user_message') {
        return c.userText || (c.contextBlocks && c.contextBlocks.length > 0);
      }
      return c.type === 'tool_call' || c.type === 'tool_result' || c.type === 'error_card';
    });
    
    if (!hasNonEmptyContent) {
      console.warn('[ConversationStore] ⏭️ Skipping persistence - no valid content');
      return;
    }
    
    // POST to backend (MUST match debouncedPersist format!)
    const { API_BASE_URL } = await import('../../../../api/config');
    const backendUrl = `${API_BASE_URL}/v2/messages`;
    if (message.role === 'user' && message.content.some((c: any) => c.type === 'tool_result')) {
      log.log('[ConversationStore] 🔍 Persisting tool_result message to backend:', {
        messageId: message.id,
        url: backendUrl,
        contentBlocks: message.content.map((c: any) => ({
          type: c.type,
          hasToolUseId: 'tool_use_id' in c,
          toolUseId: c.tool_use_id
        }))
      });
    }
    
    const response = await fetch(backendUrl, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify({
        id: message.id,
        threadId: threadId,
        skeleton: message  // Store canonical format
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[ConversationStore] Backend persist failed:', {
        status: response.status,
        error: errorText
      });
      throw new Error(`Backend persist failed: ${response.status}`);
    }
  }
  
  /**
   * Add tool call to message content (immediate notification)
   * 
   * 🆕 DEDUPLICATION: Prevents adding the same tool twice to the same message
   */
  addToolCallToMessage(threadId: string, messageId: string, toolCall: ToolCallContent): void {
    const thread = this.threads.get(threadId);
    const message = thread?.messages.get(messageId);
    if (!message) {
      console.error('[ConversationStore] Message not found for addToolCallToMessage:', {
        messageId,
        toolId: toolCall.id,
        threadId,
        availableMessages: thread ? Array.from(thread.messages.keys()) : []
      });
      return;
    }
    const existingToolCall = message.content.find((c: CanonicalContent) => {
      if (c.type === 'tool_call') {
        const existing = c as ToolCallContent;
        // Match by canonical ID, or by provider ID only when both are non-undefined.
        // Never match on undefined===undefined — headless tool calls all have
        // providerToolCallId:undefined, and that must not make them all "duplicates".
        return existing.id === toolCall.id ||
               (toolCall.providerToolCallId !== undefined &&
                existing.providerToolCallId === toolCall.providerToolCallId);
      }
      return false;
    });
    
    if (existingToolCall) {
      // Ensure the tool is indexed even if already in the message (handles race conditions)
      if (!this.toolIndex.has(toolCall.id)) {
        log.log('[ConversationStore] 🔧 Indexing duplicate tool call for lookups');
        this.toolIndex.set(toolCall.id, { threadId, messageId });
      }

      // If the existing card was created with empty input (from an early tool_call_start event)
      // and we now have the full input, update it so the card can resolve entity names.
      const existingInput = (existingToolCall as any).input ?? {};
      const newInput = toolCall.input ?? {};
      if (Object.keys(existingInput).length === 0 && Object.keys(newInput).length > 0) {
        this.updateMessage(threadId, messageId, (msg) => {
          const card = msg.content.find((c: any) => c.type === 'tool_call' && c.id === toolCall.id) as any;
          if (card) card.input = newInput;
        });
        this.notifySubscribers(threadId);
      }

      return;  // Don't add duplicate to content
    }
    
    this.updateMessage(threadId, messageId, (msg) => {
      msg.content.push(toolCall);
      
      // Index the tool call
      this.toolIndex.set(toolCall.id, { threadId, messageId });
      
      log.log('[ConversationStore] ✅ Tool call added and indexed:', {
        toolId: toolCall.id,
        toolName: toolCall.name,
        messageId,
        indexSize: this.toolIndex.size
      });
    });
    
    // Notify immediately (tool calls are important, not frequent)
    this.notifySubscribers(threadId);
    // Tool calls are part of assistant turns — tool-runtime persists them at turn end.
  }
  
  /**
   * Update tool execution state (fast lookup via index)
   */
  updateToolExecution(toolId: string, update: Partial<ToolExecutionState>): void {
    const location = this.toolIndex.get(toolId);
    if (!location) {
      console.error('[ConversationStore] Tool not found in index:', toolId);
      return;
    }
    
    const thread = this.threads.get(location.threadId);
    const message = thread?.messages.get(location.messageId);
    if (!message) {
      console.error('[ConversationStore] Message not found for tool update:', location.messageId);
      return;
    }
    
    const toolCall = message.content.find(
      (c: CanonicalContent) => c.type === 'tool_call' && c.id === toolId
    ) as ToolCallContent | undefined;
    
    if (toolCall) {
      // Rebuild content array with a new toolCall object so React.memo detects the change.
      const newContent = (message.content as any[]).map((c: any) =>
        c.type === 'tool_call' && c.id === toolId
          ? { ...c, execution: { ...c.execution, ...update } }
          : c,
      );
      thread!.messages.set(location.messageId, { ...message, content: newContent });

      this.notifySubscribers(location.threadId);
      // Tool execution updates are part of assistant turns — persisted by tool-runtime at turn end.
    }
  }
  
  /**
   * Update progress (frequent, don't persist)
   * Accepts string or structured progress object
   */
  updateToolProgress(toolId: string, progress: string | any): void {
    const location = this.toolIndex.get(toolId);
    if (!location) return;
    
    const thread = this.threads.get(location.threadId);
    const message = thread?.messages.get(location.messageId);
    if (!message) return;
    
    const toolCall = message.content.find(
      (c: CanonicalContent) => c.type === 'tool_call' && c.id === toolId
    ) as ToolCallContent | undefined;
    
    if (toolCall && toolCall.execution) {
      const newProgress = typeof progress === 'string' ? progress : { ...(typeof progress === 'object' ? progress : {}) };
      const newContent = (message.content as any[]).map((c: any) =>
        c.type === 'tool_call' && c.id === toolId
          ? { ...c, execution: { ...c.execution, progress: newProgress } }
          : c,
      );
      thread!.messages.set(location.messageId, { ...message, content: newContent });

      this.notifySubscribers(location.threadId);
      // Don't persist - too frequent
    }
  }
  
  /**
   * Append to activity log (for execution trace)
   * Builds a growing list of progress updates for tools
   */
  appendToActivityLog(toolId: string, entry: { timestamp: number; message: string; phase?: string; data?: any }): void {
    const location = this.toolIndex.get(toolId);
    if (!location) return;
    
    const thread = this.threads.get(location.threadId);
    const message = thread?.messages.get(location.messageId);
    if (!message) return;
    
    const toolCall = message.content.find(
      (c: CanonicalContent) => c.type === 'tool_call' && c.id === toolId
    ) as ToolCallContent | undefined;
    
    if (toolCall && toolCall.execution) {
      const newContent = (message.content as any[]).map((c: any) =>
        c.type === 'tool_call' && c.id === toolId
          ? { ...c, execution: { ...c.execution, activityLog: [...(c.execution.activityLog || []), entry] } }
          : c,
      );
      thread!.messages.set(location.messageId, { ...message, content: newContent });

      this.notifySubscribers(location.threadId);
    }
  }
  
  /**
   * 🆕 Update tool call input (for incremental streaming)
   * This allows us to update the input field as partial JSON is received
   * 
   * Uses toolIndex for fast lookup (messageId param might be stale in concurrent streams)
   */
  updateToolCallInput(threadId: string, messageId: string, toolId: string, partialInput: any): void {
    // 🔧 Use toolIndex instead of messageId (more reliable)
    const location = this.toolIndex.get(toolId);
    if (!location) {
      console.error('[ConversationStore] Tool not found in index for input update:', {
        toolId,
        providedMessageId: messageId,
        reason: 'tool_not_indexed'
      });
      return;
    }
    
    const thread = this.threads.get(location.threadId);
    const message = thread?.messages.get(location.messageId);
    if (!message) {
      console.error('[ConversationStore] Message not found for tool input update:', {
        toolId,
        messageIdFromIndex: location.messageId,
        providedMessageId: messageId,
        mismatch: location.messageId !== messageId
      });
      return;
    }
    
    const toolCall = message.content.find(
      (c: CanonicalContent) => c.type === 'tool_call' && c.id === toolId
    ) as ToolCallContent | undefined;
    
    if (toolCall) {
      const newContent = (message.content as any[]).map((c: any) =>
        c.type === 'tool_call' && c.id === toolId ? { ...c, input: partialInput } : c,
      );
      thread!.messages.set(location.messageId, { ...message, content: newContent });

      this.notifySubscribers(location.threadId);
    } else {
      console.error('[ConversationStore] Tool call not found in message:', {
        toolId,
        messageId: location.messageId,
        contentTypes: message.content.map(c => c.type)
      });
    }
  }
  
  /**
   * 🆕 Update tool execution view data (for live previews)
   * This updates the view field of the result for UI rendering
   * 
   * @param skipNotify - Skip notification scheduling (for render loop batching)
   */
  updateToolExecutionViewData(toolId: string, viewData: any, skipNotify = false): void {
    const location = this.toolIndex.get(toolId);
    if (!location) {
      console.error('[ConversationStore] Tool not found in index:', toolId);
      return;
    }
    
    const thread = this.threads.get(location.threadId);
    const message = thread?.messages.get(location.messageId);
    if (!message) {
      console.error('[ConversationStore] Message not found for view data update:', location.messageId);
      return;
    }
    
    const toolCall = message.content.find(
      (c: CanonicalContent) => c.type === 'tool_call' && c.id === toolId
    ) as ToolCallContent | undefined;
    
    if (toolCall && toolCall.execution) {
      const mergedView = { ...toolCall.execution.result?.view, ...viewData };
      const newResult = { ...(toolCall.execution.result ?? {}), view: mergedView };
      const newContent = (message.content as any[]).map((c: any) =>
        c.type === 'tool_call' && c.id === toolId
          ? { ...c, execution: { ...c.execution, result: newResult } }
          : c,
      );
      thread!.messages.set(location.messageId, { ...message, content: newContent });

      if (!skipNotify) {
        this.scheduleBufferedFlush(location.threadId);
      }
    } else {
      console.error('[ConversationStore] Tool call or execution not found:', { toolId, hasToolCall: !!toolCall, hasExecution: !!toolCall?.execution });
    }
  }
  
  /**
   * Get tool execution state directly
   */
  getToolExecution(toolId: string): ToolExecutionState | null {
    const location = this.toolIndex.get(toolId);
    if (!location) return null;
    
    const thread = this.threads.get(location.threadId);
    const message = thread?.messages.get(location.messageId);
    if (!message) return null;
    
    const toolCall = message.content.find(
      (c: CanonicalContent) => c.type === 'tool_call' && c.id === toolId
    ) as ToolCallContent | undefined;
    
    return toolCall?.execution || null;
  }
  
  /**
   * Get tool input from message content block
   */
  getToolInput(threadId: string, toolId: string): Record<string, any> | null {
    const location = this.toolIndex.get(toolId);
    if (!location) {
      console.warn('[ConversationStore] getToolInput - tool not in index:', toolId);
      return null;
    }
    
    const thread = this.threads.get(location.threadId);
    const message = thread?.messages.get(location.messageId);
    if (!message) {
      console.warn('[ConversationStore] getToolInput - message not found:', location.messageId);
      return null;
    }
    
    const toolCall = message.content.find(
      (c: CanonicalContent) => c.type === 'tool_call' && c.id === toolId
    ) as ToolCallContent | undefined;
    
    return toolCall?.input || null;
  }
  
  /**
   * Get abort signal for tool execution
   */
  getAbortSignal(toolId: string): AbortSignal {
    let controller = this.abortControllers.get(toolId);
    if (!controller) {
      controller = new AbortController();
      this.abortControllers.set(toolId, controller);
    }
    return controller.signal;
  }
  
  /**
   * Cancel tool execution
   */
  cancelTool(toolId: string): void {
    this.abortControllers.get(toolId)?.abort();
    this.abortControllers.delete(toolId);
    
    // Update tool state to cancelled
    this.updateToolExecution(toolId, {
      status: 'cancelled',
      endTime: Date.now()
    });
  }
  
  /**
   * Replace entire thread (for reconciliation)
   * ✅ CRITICAL FIX: Preserve in-progress streaming messages to prevent race condition
   */
  /**
   * Synthesise ToolExecutionState for tool_call blocks that came back from MongoDB
   * without an execution field (backend only stores bare tool_call + tool_result pairs).
   *
   * Strategy:
   *  1. Scan every user message for tool_result blocks and build a lookup map.
   *  2. Walk every assistant message's tool_call blocks; any that lack an `execution`
   *     get a synthetic one built from the matching tool_result (status=completed or error).
   */
  private synthesizeExecutionStates(messages: CanonicalMessage[]): void {
    // Build tool_use_id → tool_result content map from all user messages
    const toolResults = new Map<string, { content: any; timestamp?: number }>();
    for (const msg of messages) {
      if (msg.role !== 'user') continue;
      const ts = msg.timestamp ? new Date(msg.timestamp as any).getTime() : Date.now();
      for (const block of msg.content) {
        if ((block as any).type === 'tool_result') {
          const tr = block as any;
          toolResults.set(tr.tool_use_id, { content: tr.content, timestamp: ts });
        }
      }
    }

    // Walk assistant messages and patch bare tool_call blocks
    let synthesized = 0;
    for (const msg of messages) {
      if (msg.role !== 'assistant') continue;
      for (const block of msg.content) {
        if ((block as any).type !== 'tool_call') continue;
        const tc = block as any;
        if (tc.execution) continue; // already has execution state — leave it alone

        const match = toolResults.get(tc.id);
        if (!match) {
          // No tool_result found — mark as error so the card still renders
          tc.execution = {
            status: 'error',
            startTime: msg.timestamp ? new Date(msg.timestamp as any).getTime() : Date.now(),
            endTime: msg.timestamp ? new Date(msg.timestamp as any).getTime() : Date.now(),
            error: 'Result not available (interrupted or pre-history)',
          };
          synthesized++;
          continue;
        }

        const contentStr = typeof match.content === 'string'
          ? match.content
          : JSON.stringify(match.content);

        let parsed: any = null;
        try { parsed = JSON.parse(contentStr); } catch { /* raw string */ }

        const isError = parsed?.success === false || parsed?.error != null || parsed?.isError === true;
        const ts = match.timestamp ?? Date.now();

        tc.execution = {
          status: isError ? 'error' : 'completed',
          startTime: ts,
          endTime: ts,
          // Mirror the live-path shape from conversationYjsStore tool_result handler:
          // { ...event.result, rawJson, raw: event.result }
          // This ensures renderCompleted works the same for reload as for live runs.
          result: parsed != null
            ? { ...parsed, rawJson: contentStr, raw: parsed }
            : { rawJson: contentStr, raw: { message: contentStr } },
          ...(isError ? { error: parsed?.error ?? parsed?.message ?? 'Tool returned an error' } : {}),
        };
        synthesized++;
      }
    }

    if (synthesized > 0) {
      console.log(`[ConversationStore] 🔧 Synthesized execution state for ${synthesized} bare tool_call blocks`);
    }
  }

  /**
   * Public entry point to run synthesizeExecutionStates against all messages
   * currently stored for a thread. Called after batch-seeding from Y.Doc to
   * ensure tool cards show correct execution state on first load.
   */
  synthesizeExecutionStatesForThread(threadId: string): void {
    const thread = this.threads.get(threadId);
    if (!thread) return;
    const messages = thread.messageIds.map(id => thread.messages.get(id)!).filter(Boolean);
    this.synthesizeExecutionStates(messages);
  }

  replaceThread(threadId: string, messages: CanonicalMessage[], options?: { preserveInProgress?: boolean }): void {
    log.log('[ConversationStore] Replacing thread:', threadId, 'with', messages.length, 'messages');
    // Deduplicate by ID — Y.Doc CRDT merges can produce duplicate array entries
    const seenIds = new Set<string>();
    const normalizedMessages = messages
      .map(msg => this.normalizeToolResultFields(msg))
      .filter(msg => {
        if (seenIds.has(msg.id)) return false;
        seenIds.add(msg.id);
        return true;
      });
    // stores bare tool_call + tool_result pairs — execution is a frontend runtime concept).
    this.synthesizeExecutionStates(normalizedMessages);
    const existingThread = this.threads.get(threadId);
    const inProgressMessages = new Map<string, CanonicalMessage>();
    
    // Only preserve local-but-not-backend messages when there is a live active run.
    // Without an active run, any local message absent from the backend is stale
    // (e.g. a Y.Doc message from a previous session) and must be discarded.
    if (existingThread && options?.preserveInProgress) {
      const backendMessageIds = new Set(normalizedMessages.map(m => m.id));
      
      for (const [msgId, msg] of existingThread.messages.entries()) {
        // If message exists locally but not in backend, it's in-progress
        if (!backendMessageIds.has(msgId)) {
          log.log('[ConversationStore] Preserving in-progress message:', msgId);
          inProgressMessages.set(msgId, this.normalizeToolResultFields(msg));
        }
      }
    }
    
    // Clear old tool index entries for this thread
    for (const [toolId, location] of this.toolIndex.entries()) {
      if (location.threadId === threadId) {
        this.toolIndex.delete(toolId);
      }
    }
    
    // Build new thread state
    const thread: ThreadData = { messageIds: [], messages: new Map() };
    for (const msg of normalizedMessages) {
      // Check if this message exists locally with execution state
      const existingMsg = existingThread?.messages.get(msg.id);
      
      msg.content.forEach((block: CanonicalContent) => {
        if (block.type === 'tool_call') {
          const backendExecution = (block as any).execution;
          if (!backendExecution) return;

          const existingBlock = existingMsg?.content.find(
            (c: CanonicalContent) => c.type === 'tool_call' && c.id === block.id
          );
          const localExecution = existingBlock?.type === 'tool_call' ? (existingBlock as any).execution : null;

          const TERMINAL = ['completed', 'error', 'cancelled'] as const;
          const INTERMEDIATE = ['executing', 'queued'] as const;
          const localIsTerminal = TERMINAL.includes(localExecution?.status);
          const backendIsIntermediate = INTERMEDIATE.includes(backendExecution.status);

          if (localIsTerminal && backendIsIntermediate) {
            // Local finished but backend hasn't persisted yet — keep local result
            log.log('[ConversationStore] Preserving local terminal status over stale backend:', {
              toolId: (block as any).id,
              localStatus: localExecution.status,
              backendStatus: backendExecution.status,
            });
            backendExecution.status = localExecution.status;
            backendExecution.endTime = localExecution.endTime;
            backendExecution.result = localExecution.result;
            if (localExecution.error) backendExecution.error = localExecution.error;
          } else if (backendIsIntermediate) {
            // No live execution context will ever complete this — mark as interrupted
            log.log('[ConversationStore] Normalizing stale executing tool to error on load:', (block as any).id);
            backendExecution.status = 'error';
            backendExecution.error = 'Interrupted — session ended before completion';
            backendExecution.endTime = Date.now();
          } else if (localExecution?.result?.view && Object.keys(localExecution.result.view).length > 0) {
            // Tool is already terminal — preserve local view data (rich rendering)
            log.log('[ConversationStore] Preserving client-side view data for tool:', (block as any).id);
            if (!backendExecution.result) backendExecution.result = { view: {} };
            if (!backendExecution.result.view) backendExecution.result.view = {};
            backendExecution.result.view = {
              ...backendExecution.result.view,
              ...localExecution.result.view,
            };
          }
        }
      });
      
      thread.messages.set(msg.id, msg);
      thread.messageIds.push(msg.id);
      
      // Rebuild tool index
      msg.content.forEach((block: CanonicalContent) => {
        if (block.type === 'tool_call') {
          this.toolIndex.set(block.id, { threadId, messageId: msg.id });
        }
      });
    }
    for (const [msgId, msg] of inProgressMessages.entries()) {
      thread.messages.set(msgId, msg);
      thread.messageIds.push(msgId);
      
      // Rebuild tool index for in-progress messages too
      msg.content.forEach((block: CanonicalContent) => {
        if (block.type === 'tool_call') {
          this.toolIndex.set(block.id, { threadId, messageId: msg.id });
        }
      });
    }
    
    if (inProgressMessages.size > 0) {
      log.log('[ConversationStore] ✅ Preserved', inProgressMessages.size, 'in-progress messages during thread replacement');
    }
    
    this.threads.set(threadId, thread);

    // ── Chat health summary ──────────────────────────────────────────────────
    const allToolBlocks: Array<{ id: string; toolName: string; status: string; hasInput: boolean; hasResult: boolean; hasEntityId: boolean }> = [];
    for (const msg of thread.messages.values()) {
      msg.content.forEach((block: CanonicalContent) => {
        if (block.type === 'tool_call') {
          const b = block as any;
          allToolBlocks.push({
            id: (b.id as string).slice(-8),
            toolName: b.tool_name || b.name || '?',
            status: b.execution?.status || 'no-execution',
            hasInput: !!(b.input && Object.keys(b.input).length > 0),
            hasResult: !!(b.execution?.result),
            hasEntityId: !!(b.input?.entityId || b.input?.id || b.input?.llmid || b.input?.documentId || b.input?.spreadsheetId),
          });
        }
      });
    }
    const statusCounts = allToolBlocks.reduce((acc, t) => { acc[t.status] = (acc[t.status] || 0) + 1; return acc; }, {} as Record<string, number>);
    const missingInput = allToolBlocks.filter(t => !t.hasInput).map(t => `${t.toolName}(…${t.id})`);
    const missingEntityId = allToolBlocks.filter(t => !t.hasEntityId && ['doc_write','note_write','sheet_write','entity_rename','entity_move','entity_open','entity_delete'].includes(t.toolName));
    console.log(`[ConversationStore] 🏥 Thread health after load — ${thread.messageIds.length} msgs, ${allToolBlocks.length} tool calls`, {
      threadId,
      statusCounts,
      missingInputTools: missingInput.length > 0 ? missingInput : 'none',
      missingEntityIdOnEntityTools: missingEntityId.length > 0 ? missingEntityId.map(t => `${t.toolName}(…${t.id})`) : 'none',
      toolDetail: allToolBlocks,
    });
    // ── end health summary ───────────────────────────────────────────────────

    this.notifySubscribers(threadId);
    
    // Trigger reconciliation callbacks
    this.onReconcileCallbacks.forEach(callback => {
      try {
        callback(threadId);
      } catch (error) {
        console.error('[ConversationStore] Reconciliation callback error:', error);
      }
    });
  }
  
  /**
   * Rename a thread ID in-place — used after optimistic thread creation when the backend
   * returns a different (MongoDB-assigned) ID than the local client-generated one.
   *
   * Migrates threads, subscribers, toolIndex, persistenceQueue, dirtyMessages, and
   * streamingBuffers. Any messages whose `threadId` field points to the old ID are
   * updated to the new ID so cross-store consistency checks don't fire false alarms.
   * Notifies subscribers on the NEW thread ID so the UI re-renders immediately.
   */
  renameThread(oldThreadId: string, newThreadId: string): void {
    if (oldThreadId === newThreadId) return;

    const thread = this.threads.get(oldThreadId);
    if (thread) {
      // Update the threadId field on every message
      for (const msg of thread.messages.values()) {
        if ((msg as any).threadId === oldThreadId) {
          (msg as any).threadId = newThreadId;
        }
      }
      this.threads.set(newThreadId, thread);
      this.threads.delete(oldThreadId);
    }

    // Move subscribers — only if the old set is non-empty so we never overwrite a live
    // subscriber (registered under the new backendId) with a stale empty set from localId.
    const subs = this.subscribers.get(oldThreadId);
    if (subs && subs.size > 0) {
      const existing = this.subscribers.get(newThreadId);
      if (existing) {
        subs.forEach(cb => existing.add(cb));
      } else {
        this.subscribers.set(newThreadId, subs);
      }
      this.subscribers.delete(oldThreadId);
    } else if (subs) {
      // Empty set — just clean it up
      this.subscribers.delete(oldThreadId);
    }

    // Update toolIndex entries that reference the old threadId
    for (const [toolId, loc] of this.toolIndex.entries()) {
      if (loc.threadId === oldThreadId) {
        this.toolIndex.set(toolId, { ...loc, threadId: newThreadId });
      }
    }

    // Streaming buffers
    const buf = this.streamingBuffers.get(oldThreadId);
    if (buf) {
      this.streamingBuffers.set(newThreadId, buf);
      this.streamingBuffers.delete(oldThreadId);
    }

    // AbortControllers
    const ctrl = this.abortControllers.get(oldThreadId);
    if (ctrl) {
      this.abortControllers.set(newThreadId, ctrl);
      this.abortControllers.delete(oldThreadId);
    }

    log.log(`[ConversationStore] renameThread: ${oldThreadId.slice(-8)} → ${newThreadId.slice(-8)}`);

    // Notify on the new ID so any subscriber still holding the old unsubscribe closure is
    // updated. (SpaceChat will re-subscribe once chatStore emits the new currentThreadId.)
    this.notifySubscribers(newThreadId);
  }

  /**
   * Find a message across all threads by messageId
   * Useful when you have a messageId but don't know which thread it belongs to
   * 
   * @param messageId The message ID to search for
   * @returns Object with threadId and message, or null if not found
   */
  findMessageAcrossThreads(messageId: string): { threadId: string; message: CanonicalMessage } | null {
    for (const [threadId, thread] of this.threads) {
      const message = thread.messages.get(messageId);
      if (message) {
        return { threadId, message };
      }
    }
    return null;
  }
  
  /**
   * Backend persistence (automatic, debounced with dirty tracking)
   * Public for StreamHandler to trigger final persistence after streaming completes
   */
  async persistMessage(threadId: string, messageId: string): Promise<void> {
    // Mark as dirty
    this.dirtyMessages.add(messageId);
    
    // Debounce rapid updates
    if (this.persistenceQueue.has(messageId)) {
      return;
    }
    
    const persistPromise = this.debouncedPersist(threadId, messageId);
    this.persistenceQueue.set(messageId, persistPromise);
    
    try {
      await persistPromise;
    } catch (error) {
      console.error('[ConversationStore] Persistence failed:', error);
      // StateReconciliation will fix on reconnect
    } finally {
      this.persistenceQueue.delete(messageId);
      
      // If still dirty, persist again
      if (this.dirtyMessages.has(messageId)) {
        this.persistMessage(threadId, messageId);
      }
    }
  }
  
  private async debouncedPersist(threadId: string, messageId: string): Promise<void> {
    // Wait 500ms before persisting
    await new Promise(resolve => setTimeout(resolve, 500));
    
    let thread = this.threads.get(threadId);
    let message = thread?.messages.get(messageId);

    // The thread may have been renamed (optimistic local ID → backend ID) during the
    // debounce window. Search across all threads so the message is not silently dropped.
    if (!message) {
      const found = this.findMessageAcrossThreads(messageId);
      if (!found) return;
      thread = this.threads.get(found.threadId);
      message = found.message;
      threadId = found.threadId;
    }
    // These are placeholders created before streaming that never got content
    if (message.role === 'assistant' && message.content.length === 0) {
      console.warn('[ConversationStore] ⏭️ Skipping persistence of empty assistant message:', {
        messageId: message.id,
        threadId,
        reason: 'empty_content_array'
      });
      this.dirtyMessages.delete(messageId);
      return;
    }
    const hasNonEmptyContent = message.content.some((c: any) => {
      if (c.type === 'text') {
        return c.text && c.text.trim().length > 0;
      }
      if (c.type === 'user_message') {
        return c.userText || (c.contextBlocks && c.contextBlocks.length > 0);
      }
      // Tool calls, tool results, error cards, and buttons are valid content even if text is empty
      return c.type === 'tool_call' || c.type === 'tool_result' || c.type === 'error_card' || c.type === 'button';
    });
    
    if (!hasNonEmptyContent) {
      console.warn('[ConversationStore] ⏭️ Skipping persistence of message with no non-empty content:', {
        messageId: message.id,
        threadId,
        role: message.role,
        contentLength: message.content.length,
        contentTypes: message.content.map((c: any) => c.type)
      });
      this.dirtyMessages.delete(messageId);
      return;
    }
    
    try {
      const contentSummary = {
        contentLength: message.content.length,
        contentTypes: message.content.map((c: any) => c.type),
        textLengths: message.content
          .filter((c: any) => c.type === 'text')
          .map((c: any) => c.text?.length || 0),
        hasNonEmptyText: message.content.some((c: any) => 
          c.type === 'text' && c.text && c.text.trim().length > 0
        )
      };
      
      log.log('[ConversationStore] 💾 Persisting message to backend:', {
        messageId: message.id.substring(0, 20) + '...',
        threadId: threadId.substring(0, 20) + '...',
        role: message.role,
        ...contentSummary
      });
      
      // POST to backend (canonical format)
      // Import API_BASE_URL for consistent backend communication
      const { API_BASE_URL } = await import('../../../../api/config');
      const backendUrl = `${API_BASE_URL}/v2/messages`;
      
      const response = await fetch(backendUrl, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          id: message.id,
          threadId: threadId,
          skeleton: message  // Store canonical format
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[ConversationStore] Backend persist failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText,
          messageId: message.id
        });
        throw new Error(`Backend persist failed: ${response.status} ${response.statusText}`);
      }
      
      log.log('[ConversationStore] ✅ Message persisted successfully:', {
        messageId: message.id.substring(0, 20) + '...',
        ...contentSummary
      });
      this.dirtyMessages.delete(messageId);
    } catch (error) {
      console.error('[ConversationStore] Backend persist failed:', error);
      // Re-mark dirty so persistMessage's retry loop re-triggers on next opportunity
      this.dirtyMessages.add(messageId);
    }
  }
  
  /**
   * Subscribe to thread updates
   */
  subscribe(threadId: string, callback: Callback): () => void {
    if (!threadId) {
      console.warn('[ConvStore] subscribe called with empty threadId — returning no-op');
      return () => {};
    }
    if (!this.subscribers.has(threadId)) {
      this.subscribers.set(threadId, new Set());
    }
    
    this.subscribers.get(threadId)!.add(callback);
    console.log(`[ConvStore] ✅ subscribe: registered for ...${threadId?.slice(-6)}, total=${this.subscribers.get(threadId)!.size}`);
    
    // Immediately call with current state
    const messages = this.getMessages(threadId);
    callback(messages);
    
    // Return unsubscribe function
    return () => {
      console.log(`[ConvStore] 🗑️ unsubscribe: removed for ...${threadId?.slice(-6)}`);
      const set = this.subscribers.get(threadId);
      if (set) {
        set.delete(callback);
        // Remove the empty Set so renameThread cannot overwrite a live subscriber
        // with a stale empty Set from the old localId.
        if (set.size === 0) {
          this.subscribers.delete(threadId);
        }
      }
    };
  }
  
  /**
   * Notify subscribers of thread updates.
   * Public for StreamHandler render loop to bypass buffered flush.
   */
  notifySubscribers(threadId: string): void {
    const callbacks = this.subscribers.get(threadId);
    if (!callbacks) return;
    
    const messages = this.getMessages(threadId);
    callbacks.forEach(cb => {
      try {
        cb(messages);
      } catch (error) {
        console.error('[ConversationStore] Subscriber callback failed:', error);
      }
    });
  }

  /**
   * Notify subscribers only if the thread has changed since the last
   * subscriber invocation.  Uses the version counter to skip no-op flushes.
   */
  private notifySubscribersIfChanged(threadId: string, sinceVersion: number): void {
    const current = this.threadVersions.get(threadId) ?? 0;
    if (current > sinceVersion) {
      this.notifySubscribers(threadId);
    }
  }
  
  /**
   * Register callback for reconciliation events
   */
  onReconcile(callback: (threadId: string) => void): () => void {
    this.onReconcileCallbacks.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.onReconcileCallbacks.indexOf(callback);
      if (index > -1) {
        this.onReconcileCallbacks.splice(index, 1);
      }
    };
  }
  
  /**
   * Check if thread needs reconciliation (has pending persistence)
   */
  hasPendingPersistence(threadId: string): boolean {
    const thread = this.threads.get(threadId);
    if (!thread) return false;
    
    // Check if any messages in this thread are in the persistence queue
    for (const messageId of thread.messageIds) {
      if (this.persistenceQueue.has(messageId) || this.dirtyMessages.has(messageId)) {
        return true;
      }
    }
    
    return false;
  }
  
  /**
   * Wait for all pending persistence to complete
   */
  async waitForPersistence(threadId?: string): Promise<void> {
    const promises: Promise<void>[] = [];
    
    if (threadId) {
      const thread = this.threads.get(threadId);
      if (thread) {
        for (const messageId of thread.messageIds) {
          const promise = this.persistenceQueue.get(messageId);
          if (promise) {
            promises.push(promise);
          }
        }
      }
    } else {
      // Wait for all persistence
      promises.push(...Array.from(this.persistenceQueue.values()));
    }
    
    await Promise.all(promises);
  }
  
  /**
   * Synchronously flush all dirty (user) messages via fetch keepalive.
   * Called on beforeunload — keepalive: true tells the browser to complete
   * the request even after the page unloads, unlike a plain async fetch.
   * Only user messages are flushed here; assistant messages are persisted
   * by the tool-runtime.
   */
  flushDirtyMessages(): void {
    if (this.dirtyMessages.size === 0) return;
    const authToken = localStorage.getItem('authToken');
    // API_BASE_URL is a synchronous constant; use a synchronous import here.
    // We can't use dynamic import() inside beforeunload.
    const backendUrl = `${window.location.origin}/api/v2/messages`;

    for (const messageId of this.dirtyMessages) {
      const threadId = this._findThreadForMessage(messageId);
      if (!threadId) continue;
      const thread = this.threads.get(threadId);
      const message = thread?.messages.get(messageId);
      if (!message || message.role === 'assistant') continue;

      try {
        fetch(backendUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
          },
          body: JSON.stringify({ id: message.id, threadId, skeleton: message }),
          keepalive: true,  // survives page unload
        }).catch(() => {/* best-effort; cannot handle errors after unload */});
      } catch {
        // Cannot throw during beforeunload
      }
    }
  }

  /** Find the thread that contains a given messageId. */
  private _findThreadForMessage(messageId: string): string | null {
    for (const [threadId, thread] of this.threads.entries()) {
      if (thread.messages.has(messageId)) return threadId;
    }
    return null;
  }

  /**
   * DEV ONLY: Remove a preview thread and all its tool index entries without
   * triggering persistence, Y.doc writes, or subscriber notifications.
   * Used by ToolCardPreview to clean up injected fake state on unmount.
   */
  clearPreviewThread(threadId: string): void {
    for (const [toolId, loc] of this.toolIndex.entries()) {
      if (loc.threadId === threadId) this.toolIndex.delete(toolId);
    }
    this.threads.delete(threadId);
    this.subscribers.delete(threadId);
  }

  /**
   * Clear all state (for testing)
   */
  clear(): void {
    this.threads.clear();
    this.subscribers.clear();
    this.toolIndex.clear();
    this.abortControllers.clear();
    this.persistenceQueue.clear();
    this.dirtyMessages.clear();
    this.onReconcileCallbacks = [];
  }
}

// Singleton instance
export const conversationStore = new ConversationStore();

// Flush any dirty messages when the user closes or navigates away.
// Uses fetch with keepalive:true so the request outlives the page.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    conversationStore.flushDirtyMessages();
  });
}

